import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { BundlePayStep } from '../embed/ResellerEmbed'

/**
 * Payment Setup — Phase 4.
 *
 * Mode picker (A or B), then conditional sections:
 *   - Mode A → bank info form (Canadian institution/transit/account)
 *   - Mode B → gateway creds form (Stripe / Moneris / PayPal) + card on file
 *
 * All sensitive inputs are write-only — backend returns only display-safe
 * previews on GET (last3 of account, last4 of card, "configured" booleans).
 */
export default function PaymentSetup() {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/payment')
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  async function setMode(mode) {
    try { await tournamentApi.put('/portal/reseller/payment/mode', { mode }); reload() }
    catch (err) { alert(err?.response?.data?.message || err.message) }
  }

  if (loading) return <ResellerShell title={t('sp_payment_setup_title', 'Payment setup')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  // Managed children don't choose a mode — they inherit the parent's. Show an
  // informational payout view instead of the A/B chooser.
  if (data?.managedChild?.isChild) {
    return (
      <ResellerShell title={t('sp_payment_setup_title', 'Payment setup')}>
        {error && <div style={errorBox}>{error}</div>}
        <ManagedChildPayment managedChild={data.managedChild} bank={data.bank} onChange={reload} />
      </ResellerShell>
    )
  }

  return (
    <ResellerShell title={t('sp_payment_setup_title', 'Payment setup')}>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 24, fontSize: 14, maxWidth: 720 }}>
        {t('sp_payment_intro', 'Choose how customer payments flow. You can change this later, but most resellers pick once and stick with it. All sensitive credentials are encrypted at rest using AES-256-GCM.')}
      </p>
      {error && <div style={errorBox}>{error}</div>}

      <h2 style={h2}>{t('sp_payment_mode', 'Payment mode')}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 14 }}>
        <ModeCard
          mode="A" active={data?.mode === 'A'} onPick={() => setMode('A')}
          title={t('sp_mode_a_title', 'A — InteGolf Payments')}
          subtitle={t('sp_mode_a_subtitle', '5% platform + 2.99% gateway fee')}
          bullets={[
            t('sp_mode_a_bullet_pays_integolf', 'Customer pays InteGolf via our Moneris'),
            t('sp_mode_a_bullet_we_pay_vendor', 'We pay vendor costs (hotel + golf when applicable)'),
            t('sp_mode_a_bullet_eft_net', 'EFT the net to your bank weekly'),
            t('sp_mode_a_bullet_need_bank', 'Need: your bank info'),
          ]}
        />
        <ModeCard
          mode="B" active={data?.mode === 'B'} onPick={() => setMode('B')}
          title={t('sp_mode_b_title', 'B — Your own gateway')}
          subtitle={t('sp_mode_b_subtitle', 'No InteGolf processing fee on customer side')}
          bullets={[
            t('sp_mode_b_bullet_pays_you', 'Customer pays you via your gateway (Stripe or Moneris)'),
            t('sp_mode_b_bullet_money_lands', 'Money lands in your account'),
            t('sp_mode_b_bullet_charge_card', 'We charge your card on file for the hotel (all-in) + our platform fee'),
            t('sp_mode_b_bullet_need_creds', 'Need: gateway creds + card on file'),
          ]}
        />
      </div>

      {/* Fees sit directly under the A/B cards so the whole payment-mode
          choice (mode + your fees + how they display) reads as one block,
          before the mode-specific bank/gateway details below. */}
      {data?.mode && <FeeDisplaySection data={data} onChange={reload} />}

      {data?.mode === 'A' && <BankInfoSection bank={data.bank} onChange={reload} />}
      {data?.mode === 'B' && (
        <>
          <GatewaySection gateway={data.gateway} onChange={reload} />
          <CardOnFileSection card={data.card} onChange={reload} />
        </>
      )}
      {!data?.mode && <div style={empty}>{t('sp_pick_payment_mode', 'Pick a payment mode above to continue.')}</div>}
    </ResellerShell>
  )
}

/**
 * Managed-child payout view. The child inherits the parent's payment mode:
 *   - parent Mode A (InteGolf collects) → InteGolf pays the child directly, so
 *     the child enters their own bank info here.
 *   - parent Mode B (own gateway)       → the parent collects + pays the child;
 *     nothing for the child to configure with InteGolf.
 *   - parent not set up yet             → informational holding message.
 */
function ManagedChildPayment({ managedChild, bank, onChange }) {
  const { t } = useLanguage()
  const parent = managedChild.parentName || t('sp_pay_child_your_partner', 'your partner')
  const mode = managedChild.parentMode

  const banner = { padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 14, color: '#1e3a8a', marginBottom: 20, lineHeight: 1.5 }
  const note = { padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 20 }

  return (
    <>
      <div style={banner}>
        {t('sp_pay_child_intro', 'Your account is managed by {parent}. How you get paid follows their payment setup — you don’t choose a payment mode here.', { parent })}
      </div>

      {mode === 'A' && (
        <>
          <div style={note}>
            {t('sp_pay_child_mode_a', 'Payments are processed by InteGolf and paid out to you directly. Add your bank details below so we can send your share.')}
          </div>
          <BankInfoSection bank={bank} onChange={onChange} />
        </>
      )}

      {mode === 'B' && (
        <div style={note}>
          {t('sp_pay_child_mode_b', '{parent} processes payments for your storefront and pays you out directly. Contact {parent} for your payout details — there’s nothing to set up with InteGolf here.', { parent })}
        </div>
      )}

      {!mode && (
        <div style={note}>
          {t('sp_pay_child_pending', '{parent} hasn’t finished setting up payments yet. Once they do, this page will show how you get paid.', { parent })}
        </div>
      )}
    </>
  )
}

/**
 * Reseller-owned fees (Mode B) + the "combine into one Booking Fee" display
 * toggle (both modes). The Platform/Gateway % fields only show in Mode B —
 * those are the reseller's own cut on packages they self-host. Saved together
 * via PUT /payment/reseller-fees.
 */
function FeeDisplaySection({ data, onChange }) {
  const { t } = useLanguage()
  const isB = data?.mode === 'B'
  const [platform, setPlatform] = useState(data?.resellerPlatformFeePercent != null ? String(data.resellerPlatformFeePercent) : '')
  const [gateway, setGateway] = useState(data?.resellerGatewayFeePercent != null ? String(data.resellerGatewayFeePercent) : '')
  const [combine, setCombine] = useState(!!data?.combineFeesAsBookingFee)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(false)

  async function save() {
    setSaving(true)
    try {
      await tournamentApi.put('/portal/reseller/payment/reseller-fees', {
        // In Mode A the reseller fee %s aren't editable here; preserve stored values.
        platformPercent: isB ? (platform === '' ? null : Number(platform)) : (data?.resellerPlatformFeePercent ?? null),
        gatewayPercent:  isB ? (gateway === '' ? null : Number(gateway))  : (data?.resellerGatewayFeePercent ?? null),
        combineFees: combine,
      })
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500)
      onChange()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #e2e8f0' }}>
      <SectionHeader
        title={t('sp_fees_section_title', 'Fees')}
        subtitle={isB
          ? t('sp_fees_section_sub_b', "Your own fees on packages you host yourself. Like InteGolf's, they're a percentage of the package total — your revenue, carved from the price (no extra charge to the customer).")
          : t('sp_fees_section_sub_a', 'Control how fees appear in your package cost breakdown.')}
      />
      {isB && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
          <Field label={t('sp_fee_platform_pct', 'Your platform fee (%)')} hint={t('sp_fee_pct_hint', 'Leave blank for none.')}>
            <input style={inp} type="number" min={0} max={100} step="0.01" value={platform} onChange={e => setPlatform(e.target.value)} placeholder="5" />
          </Field>
          <Field label={t('sp_fee_gateway_pct', 'Your gateway fee (%)')} hint={t('sp_fee_pct_hint', 'Leave blank for none.')}>
            <input style={inp} type="number" min={0} max={100} step="0.01" value={gateway} onChange={e => setGateway(e.target.value)} placeholder="2.99" />
          </Field>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <Toggle checked={combine} onChange={setCombine} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={() => setCombine(c => !c)}>
            {t('sp_fee_combine_label', 'Combine fees into a single "Booking Fee"')}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {t('sp_fee_combine_hint', 'Shows one "Booking Fee" line (the sum of all fees) instead of itemizing them. Applies to both payment modes.')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1 }}>
          {saving ? t('sp_saving', 'Saving…') : t('sp_fee_save', 'Save fees')}
        </button>
        {savedAt && <span style={{ color: '#166534', fontSize: 13, fontWeight: 600 }}>{t('sp_saved', 'Saved')}</span>}
      </div>
    </div>
  )
}

function ModeCard({ mode, active, onPick, title, subtitle, bullets }) {
  const { t } = useLanguage()
  return (
    <div onClick={onPick} style={{
      cursor: 'pointer', padding: 20, background: '#fff',
      border: active ? '2px solid #00d19f' : '1px solid #e2e8f0',
      borderRadius: 8, position: 'relative',
    }}>
      {active && <div style={{ position: 'absolute', top: 12, right: 12, fontSize: 11, fontWeight: 700, color: '#00d19f', letterSpacing: 1 }}>{t('sp_chip_active', 'ACTIVE')}</div>}
      <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#0f172a' }}>{title}</h3>
      <div style={{ fontSize: 12, color: '#00d19f', fontWeight: 600, marginBottom: 12 }}>{subtitle}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </div>
  )
}

// ─── Bank info (Mode A) ─────────────────────────────────────────────────

function BankInfoSection({ bank, onChange }) {
  const { t } = useLanguage()
  const [editing, setEditing] = useState(!bank?.configured)
  return (
    <div style={section}>
      <SectionHeader title={t('sp_bank_section_title', 'Bank info (for EFT payouts)')} subtitle={t('sp_bank_section_subtitle', 'Canadian banking trio: institution + transit + account number.')} />
      {bank?.configured && !editing
        ? (
          <div>
            <Row label={t('sp_account_holder', 'Account holder')} value={bank.holderNamePreview} />
            <Row label={t('sp_account_ending_in', 'Account ending in')} value={`***${bank.accountLast3}`} />
            <Row label={t('sp_currency', 'Currency')} value={bank.currency} />
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(true)} style={primaryBtn}>{t('sp_replace', 'Replace')}</button>
              <button onClick={async () => {
                if (!confirm(t('sp_remove_bank_confirm', 'Remove your bank info?'))) return
                try { await tournamentApi.delete('/portal/reseller/payment/bank'); onChange() }
                catch (err) { alert(err?.response?.data?.message || err.message) }
              }} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove', 'Remove')}</button>
            </div>
          </div>
        )
        : <BankInfoForm onSaved={() => { setEditing(false); onChange() }} onCancel={bank?.configured ? () => setEditing(false) : null} />
      }
    </div>
  )
}

function BankInfoForm({ onSaved, onCancel }) {
  const { t } = useLanguage()
  const [institution, setInstitution] = useState('')
  const [transit, setTransit] = useState('')
  const [account, setAccount] = useState('')
  const [holderName, setHolderName] = useState('')
  const [currency, setCurrency] = useState('CAD')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  // Setup guide button rendered above the form

  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      await tournamentApi.put('/portal/reseller/payment/bank', { institution, transit, account, holderName, currency })
      onSaved()
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally { setSubmitting(false) }
  }

  return (
    <form onSubmit={submit}>
      {err && <div style={errorBox}>{err}</div>}
      <PaymentSetupGuideButton guideKey="bank" label={t('sp_bank_guide_label', 'Setup Guide — How to find your institution / transit / account numbers')} />
      <Field label={t('sp_account_holder_legal_name', 'Account holder name (legal name)')}><input style={inp} required value={holderName} onChange={e => setHolderName(e.target.value)} placeholder={t('sp_company_inc_placeholder', 'Your Company Inc.')} /></Field>
      <div style={row}>
        <Field label={t('sp_institution_3_digits', 'Institution # (3 digits)')} style={{ flex: 1 }}>
          <input style={{ ...inp, fontFamily: 'monospace' }} required maxLength={3} value={institution} onChange={e => setInstitution(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="004 (RBC), 003 (Scotia), 010 (CIBC)" />
        </Field>
        <Field label={t('sp_transit_5_digits', 'Transit # (5 digits)')} style={{ flex: 1 }}>
          <input style={{ ...inp, fontFamily: 'monospace' }} required maxLength={5} value={transit} onChange={e => setTransit(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="12345" />
        </Field>
      </div>
      <Field label={t('sp_account_number_4_12', 'Account number (4-12 digits)')}>
        <input style={{ ...inp, fontFamily: 'monospace' }} required maxLength={12} value={account} onChange={e => setAccount(e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="••••••••" />
      </Field>
      <Field label={t('sp_currency', 'Currency')}>
        <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ ...inp, width: 200 }}>
          <option value="CAD">{t('sp_cad', 'CAD')}</option>
          <option value="USD">{t('sp_usd', 'USD')}</option>
        </select>
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        {onCancel && <button type="button" onClick={onCancel} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>}
        <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving', 'Saving…') : t('sp_save_bank_info', 'Save bank info')}</button>
      </div>
    </form>
  )
}

// ─── Gateway (Mode B) ────────────────────────────────────────────────────

// Connection test for the saved gateway — validates the stored credentials with
// a card-less, no-charge probe. Result: green = connected, red = rejected,
// amber = not available (Moneris).
function GatewayTestButton({ t }) {
  const [state, setState] = useState(null) // null | { testing } | { ok, message }
  async function run() {
    setState({ testing: true })
    try {
      const { data } = await tournamentApi.post('/portal/reseller/payment/gateway/test', {})
      setState({ ok: data.ok, message: data.message })
    } catch (e) {
      setState({ ok: false, message: e?.response?.data?.message || e.message })
    }
  }
  const testing = state?.testing
  const done = state && !testing
  const palette = !done ? null
    : state.ok === true ? { c: '#166534', bg: '#f0fdf4', b: '#bbf7d0', icon: '✓' }
    : state.ok === null ? { c: '#b45309', bg: '#fffbeb', b: '#fde68a', icon: '⚠' }
    : { c: '#b91c1c', bg: '#fef2f2', b: '#fecaca', icon: '✕' }
  return (
    <div style={{ marginTop: 12 }}>
      <button type="button" onClick={run} disabled={testing}
        style={{ ...secondaryBtn, opacity: testing ? 0.6 : 1, cursor: testing ? 'default' : 'pointer' }}>
        {testing ? t('sp_pay_testing', 'Testing…') : t('sp_pay_test_btn', 'Test connection')}
      </button>
      {done && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: palette.bg, border: `1px solid ${palette.b}`, borderRadius: 6, fontSize: 12.5, color: palette.c }}>
          {palette.icon} {state.message}
        </div>
      )}
    </div>
  )
}

// Real $1 charge + immediate refund against the saved gateway, to confirm the
// full charge/refund path (not just credentials). Reuses the exact checkout card
// form (BundlePayStep) for NMI/Stripe; Moneris/Elavon get an honest message.
function GatewayTestChargeModal({ provider, t, onClose }) {
  const [cfg, setCfg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [charging, setCharging] = useState(false)
  const [result, setResult] = useState(null) // { ok, message }

  async function runCharge(body) {
    setCharging(true)
    try {
      const { data } = await tournamentApi.post('/portal/reseller/payment/gateway/test-charge', body)
      setResult({ ok: data.ok, message: data.message })
    } catch (e) {
      setResult({ ok: false, message: e?.response?.data?.message || e.message })
    } finally { setCharging(false) }
  }

  useEffect(() => {
    let alive = true
    tournamentApi.get('/portal/reseller/payment/gateway/pay-config')
      .then(r => {
        if (!alive) return
        setCfg(r.data)
        // Moneris/Elavon have no card-based test here — get the honest message.
        if (provider !== 'nmi' && provider !== 'stripe') runCharge({})
      })
      .catch(() => { if (alive) setCfg({ provider }) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const isCardGateway = provider === 'nmi' || provider === 'stripe'
  const hasKey = provider === 'nmi' ? cfg?.nmiTokenizationKey : provider === 'stripe' ? cfg?.stripePublishableKey : false
  const pal = !result ? null
    : result.ok === true ? { c: '#166534', bg: '#f0fdf4', b: '#bbf7d0', icon: '✓' }
    : result.ok === null ? { c: '#b45309', bg: '#fffbeb', b: '#fde68a', icon: '⚠' }
    : { c: '#b91c1c', bg: '#fef2f2', b: '#fecaca', icon: '✕' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 60, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 460, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{t('sp_pay_testcharge_btn', 'Test charge')}</h3>
          <button onClick={onClose} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 12px', cursor: 'pointer' }}>{t('sp_close', 'Close')}</button>
        </div>

        {result ? (
          <>
            <div style={{ padding: '10px 12px', background: pal.bg, border: `1px solid ${pal.b}`, borderRadius: 6, color: pal.c, fontSize: 13 }}>{pal.icon} {result.message}</div>
            {isCardGateway && <div style={{ marginTop: 14, textAlign: 'right' }}><button onClick={() => setResult(null)} style={secondaryBtn}>{t('sp_pay_testcharge_again', 'Test again')}</button></div>}
          </>
        ) : loading ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>{t('loading_ellipsis', 'Loading…')}</div>
        ) : isCardGateway && hasKey ? (
          <>
            <p style={{ fontSize: 13, color: '#475569', marginTop: 0 }}>
              {t('sp_pay_testcharge_intro', "We'll charge {amount} to this card and immediately refund it — a real end-to-end charge + refund test.", { amount: '$1.00' })}
            </p>
            {charging && <div style={{ fontSize: 12.5, color: '#2563eb', marginBottom: 8 }}>{t('sp_pay_charging', 'Charging…')}</div>}
            <BundlePayStep
              gateway={provider} amount={1.00} currency="CAD" primary="#0f172a" branding={null} buyerName=""
              stripePublishableKey={cfg.stripePublishableKey} nmiTokenizationKey={cfg.nmiTokenizationKey} nmiBaseUrl={cfg.nmiBaseUrl}
              onBack={onClose}
              onChargeNmi={async (token) => { await runCharge({ paymentToken: token }) }}
              onChargeStripe={async ({ paymentMethodId }) => { await runCharge({ paymentMethodId }) }}
              onChargeMoneris={() => {}} onStartElavonSession={() => {}} onChargeElavon={() => {}}
            />
          </>
        ) : isCardGateway ? (
          <div style={{ fontSize: 13, color: '#b45309' }}>{t('sp_pay_testcharge_nokey', 'This gateway is saved but its public tokenization key is missing — Replace the gateway and include it.')}</div>
        ) : (
          <div style={{ color: '#64748b', fontSize: 13 }}>{t('loading_ellipsis', 'Loading…')}</div>
        )}
      </div>
    </div>
  )
}

function GatewaySection({ gateway, onChange }) {
  const { t } = useLanguage()
  const [editing, setEditing] = useState(!gateway?.configured)
  const [showCharge, setShowCharge] = useState(false)
  return (
    <div style={section}>
      <SectionHeader title={t('sp_gateway_section_title', 'Customer-facing payment gateway')} subtitle={t('sp_gateway_section_subtitle', "Where your customers' card payments are routed. Your money lands in your account.")} />
      {gateway?.configured && !editing
        ? (
          <div>
            <Row label={t('sp_provider_row', 'Provider')} value={PROVIDER_LABELS[gateway.provider] || (gateway.provider || '').replace(/^\w/, c => c.toUpperCase())} />
            <Row label={t('sp_connected_account', 'Connected account')} value={gateway.publicLabel} />
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setEditing(true)} style={primaryBtn}>{t('sp_replace', 'Replace')}</button>
              <button onClick={async () => {
                if (!confirm(t('sp_remove_gateway_confirm', 'Remove your gateway credentials? Customer payments will fail until you replace them.'))) return
                try { await tournamentApi.delete('/portal/reseller/payment/gateway'); onChange() }
                catch (err) { alert(err?.response?.data?.message || err.message) }
              }} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove', 'Remove')}</button>
            </div>
            <GatewayTestButton t={t} />
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setShowCharge(true)} style={secondaryBtn}>{t('sp_pay_testcharge_btn', 'Test charge')}</button>
            </div>
            {showCharge && <GatewayTestChargeModal provider={gateway.provider} t={t} onClose={() => setShowCharge(false)} />}
          </div>
        )
        : <GatewayForm onSaved={() => { setEditing(false); onChange() }} onCancel={gateway?.configured ? () => setEditing(false) : null} />
      }
    </div>
  )
}

// Per-provider setup guides for payment gateways. Mirrors the email
// integration pattern. Built per-render so guide copy is translated via t().
function buildPaymentSetupGuides(t) {
  return {
  bank: {
    title: t('sp_guide_bank_title', 'Set up bank info for EFT payouts'),
    estimatedMinutes: 5,
    notes: [
      t('sp_guide_bank_note_mode_a', 'Mode A only — InteGolf collects customer payments, then EFTs your share to the bank account below.'),
      t('sp_guide_bank_note_canadian_format', "We use Canadian banking format (institution + transit + account). For USD payouts, the institution still has to be in Canada — InteGolf's bank only sends EFTs domestically."),
      t('sp_guide_bank_note_encrypted', 'Your bank info is encrypted at rest with AES-256-GCM.'),
    ],
    steps: [
      { title: t('sp_guide_bank_step_institution_title', 'Find your institution number (3 digits)'), body: t('sp_guide_bank_step_institution_body', 'Common Canadian banks: 001 BMO, 002 Scotiabank, 003 RBC, 004 TD, 006 National, 010 CIBC, 016 HSBC, 030 Canadian Western, 039 Laurentian, 117 Government of Canada. Check your void cheque or online banking for the exact number.') },
      { title: t('sp_guide_bank_step_transit_title', 'Find your transit (5 digits)'), body: t('sp_guide_bank_step_transit_body', 'On a void cheque: the 5-digit number in the bottom-left, BEFORE the 3-digit institution. Or in online banking: account details → transit/branch number.') },
      { title: t('sp_guide_bank_step_account_title', 'Find your account number (4-12 digits)'), body: t('sp_guide_bank_step_account_body', 'Bottom of a void cheque, AFTER institution + transit. Sometimes shown without separators (e.g. "001-12345-9876543").') },
      { title: t('sp_guide_bank_step_legal_name_title', 'Use your business legal name as the account holder'), body: t('sp_guide_bank_step_legal_name_body', 'Match exactly what your bank has on the account. Mismatches cause EFT rejections.') },
      { title: t('sp_guide_bank_step_void_source_title', 'Pick a void cheque source'), body: t('sp_guide_bank_step_void_source_body', 'Easiest: snap a photo of a void cheque. Second-easiest: download a "direct deposit form" (PDF) from your online banking — it lists all 3 numbers in order.') },
      { title: t('sp_guide_bank_step_save_title', 'Save and double-check'), body: t('sp_guide_bank_step_save_body', 'After saving, the page shows "Account ending in XXX" — verify the last 3 digits match what\'s on your cheque/form. If wrong, click Replace.') },
    ],
  },
  stripe: {
    title: t('sp_guide_stripe_title', 'Get your Stripe API keys'),
    estimatedMinutes: 8,
    notes: [
      t('sp_guide_stripe_note_account', "You need an active Stripe account. Sign up at stripe.com if you don't have one (no credit card needed to create an account; required to start charging)."),
      t('sp_guide_stripe_note_live_keys', "Use LIVE keys for real-money transactions. TEST keys (pk_test_…/sk_test_…) only work with Stripe's fake card numbers."),
      t('sp_guide_stripe_note_fees', 'Stripe takes 2.9% + $0.30 per successful charge (USD/CAD). Their fees come out of the customer payment before it lands in your account.'),
    ],
    steps: [
      { title: t('sp_guide_stripe_step_signin_title', 'Sign in to the Stripe Dashboard'), body: t('sp_guide_stripe_step_signin_body', 'Go to dashboard.stripe.com. Use your Stripe account credentials.'), link: { label: t('sp_guide_stripe_link_dashboard', 'dashboard.stripe.com'), url: 'https://dashboard.stripe.com/' } },
      { title: t('sp_guide_stripe_step_live_mode_title', 'Switch to LIVE mode'), body: t('sp_guide_stripe_step_live_mode_body', 'Top-left toggle. The default after sign-in is sometimes Test mode — flip it to Live for production keys.') },
      { title: t('sp_guide_stripe_step_api_keys_title', 'Open the API keys page'), body: t('sp_guide_stripe_step_api_keys_body', 'Developers → API keys (or visit dashboard.stripe.com/apikeys).'), link: { label: t('sp_guide_stripe_link_api_keys', 'API keys'), url: 'https://dashboard.stripe.com/apikeys' } },
      { title: t('sp_guide_stripe_step_pub_key_title', 'Copy the Publishable key'), body: t('sp_guide_stripe_step_pub_key_body', 'Starts with pk_live_… (or pk_test_… in test mode). Visible by default. This is the FRONTEND key.') },
      { title: t('sp_guide_stripe_step_secret_key_title', 'Reveal + copy the Secret key'), body: t('sp_guide_stripe_step_secret_key_body', 'Starts with sk_live_… (or sk_test_…). Click "Reveal live key" — it\'s shown only once per session. Treat it like a password.') },
      { title: t('sp_guide_stripe_step_paste_title', 'Paste both into InteGolf'), body: t('sp_guide_stripe_step_paste_body', 'Publishable key → first field. Secret key → second field. Click Save gateway.') },
    ],
  },
  moneris: {
    title: t('sp_guide_moneris_title', 'Get your Moneris store credentials'),
    estimatedMinutes: 10,
    notes: [
      t('sp_guide_moneris_note_account', 'You need an active Moneris merchant account. Sign up via your bank or directly at moneris.com.'),
      t('sp_guide_moneris_note_live_mode', 'Live mode = real charges. Test mode uses Moneris\' "store3"/sandbox environment with fake cards.'),
      t('sp_guide_moneris_note_fees', 'Moneris fee structure varies by your merchant agreement — typically 2.4% + $0.10 per transaction in Canada.'),
    ],
    steps: [
      { title: t('sp_guide_moneris_step_login_title', 'Log into the Moneris Merchant Resource Centre'), body: t('sp_guide_moneris_step_login_body', 'mrc.moneris.com. Use your Moneris merchant credentials.'), link: { label: t('sp_guide_moneris_link_mrc', 'Merchant Resource Centre'), url: 'https://www3.moneris.com/mpg/index.php' } },
      { title: t('sp_guide_moneris_step_store_id_title', 'Find your Store ID'), body: t('sp_guide_moneris_step_store_id_body', 'Settings → Account Settings → Store ID (sometimes called Store Name). Format is store-prefixed (e.g. "store5", "monca12345"). For test mode use "store3" with token "yesguy".') },
      { title: t('sp_guide_moneris_step_token_title', 'Generate / find your API token'), body: t('sp_guide_moneris_step_token_body', 'Admin → Store Settings → API Token. If none exists, click "Generate new" — copy it immediately.') },
      { title: t('sp_guide_moneris_step_mode_title', 'Pick mode'), body: t('sp_guide_moneris_step_mode_body', 'LIVE if you want real charges (production Moneris merchant). TEST if you want to validate the integration without billing real cards.') },
      { title: t('sp_guide_moneris_step_paste_title', 'Paste into InteGolf'), body: t('sp_guide_moneris_step_paste_body', 'Store ID → first field. API Token → second field. Mode dropdown → Live or Test. Click Save gateway.') },
      { title: t('sp_guide_moneris_step_test_title', "Test with InteGolf's test cards"), body: t('sp_guide_moneris_step_test_body', 'Once saved, run a $1 test booking. In test mode use card 4242424242424242, any expiry, any CVV. Successful approval code 027 = working.') },
    ],
  },
  paypal: {
    title: t('sp_guide_paypal_title', 'Get your PayPal API credentials'),
    estimatedMinutes: 12,
    notes: [
      t('sp_guide_paypal_note_business', 'You need a PayPal Business account (not Personal). Upgrade for free at paypal.com.'),
      t('sp_guide_paypal_note_rest_api', "PayPal's REST API uses Client ID + Secret per app you create. Live and Sandbox each have their own pair."),
      t('sp_guide_paypal_note_fees', 'PayPal fees: ~2.9% + fixed fee per transaction (varies by country/currency).'),
    ],
    steps: [
      { title: t('sp_guide_paypal_step_signin_title', 'Sign in to PayPal Developer Dashboard'), body: t('sp_guide_paypal_step_signin_body', 'developer.paypal.com → Log in with your PayPal Business credentials.'), link: { label: t('sp_guide_paypal_link_developer', 'PayPal Developer'), url: 'https://developer.paypal.com/dashboard/applications/live' } },
      { title: t('sp_guide_paypal_step_switch_title', 'Switch to Live or Sandbox'), body: t('sp_guide_paypal_step_switch_body', "Top tabs. Live for real charges, Sandbox for testing with PayPal's fake accounts.") },
      { title: t('sp_guide_paypal_step_create_app_title', 'Create a REST API app'), body: t('sp_guide_paypal_step_create_app_body', '"Apps & Credentials" tab → "Create App". Name it "InteGolf Reseller". Use "Merchant" type.') },
      { title: t('sp_guide_paypal_step_client_id_title', 'Copy the Client ID'), body: t('sp_guide_paypal_step_client_id_body', 'Shown immediately on the app detail page. Starts with "A" (live) or "AY" (sandbox).') },
      { title: t('sp_guide_paypal_step_secret_title', 'Show + copy the Client Secret'), body: t('sp_guide_paypal_step_secret_body', 'Same page → "Show" next to Secret. Starts with "EH" (live) or "EL" (sandbox).') },
      { title: t('sp_guide_paypal_step_paste_title', 'Paste into InteGolf'), body: t('sp_guide_paypal_step_paste_body', 'Client ID + Client Secret + Mode (Live or Sandbox). Click Save gateway.') },
    ],
  },
  card: {
    title: t('sp_guide_card_title', 'Adding the card we charge for outflows'),
    estimatedMinutes: 3,
    notes: [
      t('sp_guide_card_note_mode_b', 'Mode B only — when InteGolf books a hotel on your behalf (or pays a course where WhoPaysCourse=InteGolf), we charge this card via OUR Moneris merchant account.'),
      t('sp_guide_card_note_encrypted', 'The card is encrypted at rest with AES-256-GCM. CVV is NEVER stored (PCI rule).'),
      t('sp_guide_card_note_corporate', 'Use a corporate or operational card with sufficient credit limit — bookings can spike during peak season.'),
    ],
    steps: [
      { title: t('sp_guide_card_step_pick_title', 'Pick the card you want billed'), body: t('sp_guide_card_step_pick_body', 'Usually a corporate Visa/Mastercard with a high enough limit to cover hotel + golf charges over a billing cycle.') },
      { title: t('sp_guide_card_step_handy_title', 'Have it handy'), body: t('sp_guide_card_step_handy_body', 'Card number (16 digits), expiry month + year, cardholder name as printed on the card.') },
      { title: t('sp_guide_card_step_enter_title', 'Enter the details below'), body: t('sp_guide_card_step_enter_body', "Cardholder name, full card number (Luhn-validated; we'll catch typos), expiry month + 4-digit year. CVV is NOT requested.") },
      { title: t('sp_guide_card_step_save_title', 'Save'), body: t('sp_guide_card_step_save_body', 'After save, you\'ll see "VISA ending in XXXX" — verify the last 4 match. If wrong, click Replace.') },
      { title: t('sp_guide_card_step_charges_title', 'When are charges run?'), body: t('sp_guide_card_step_charges_body', 'Each Mode B customer booking that includes Expedia hotel cost or "InteGolf pays the course" rounds triggers a charge to this card immediately on confirmation.') },
    ],
  },
  }
}

function PaymentSetupGuideButton({ guideKey, label }) {
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  const guide = buildPaymentSetupGuides(t)[guideKey]
  if (!guide) return null
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', marginBottom: 14,
          background: '#eef2ff', color: '#4338ca',
          border: '1px solid #c7d2fe', borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >📖 {label || t('sp_setup_guide_prefix_inline', 'Setup Guide — {title}', { title: guide.title })}</button>
      {open && <PaymentSetupGuideModal guide={guide} onClose={() => setOpen(false)} />}
    </>
  )
}

function PaymentSetupGuideModal({ guide, onClose }) {
  const { t } = useLanguage()
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, maxWidth: 640, width: '100%',
        maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '1.6rem' }}>📖</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>{guide.title}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>{t('sp_guide_minutes_steps', '⏱ ~{minutes} minutes · {count} steps', { minutes: guide.estimatedMinutes, count: guide.steps.length })}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9ca3af', padding: 0, lineHeight: 1 }}>×</button>
        </div>
        {guide.notes?.length > 0 && (
          <div style={{ margin: '16px 24px 0', padding: '12px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.85rem', marginBottom: 6 }}>{t('sp_before_you_start', 'Before you start')}</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.82rem', color: '#78350f', lineHeight: 1.55 }}>
              {guide.notes.map((n, i) => <li key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>{n}</li>)}
            </ul>
          </div>
        )}
        <ol style={{ padding: '20px 24px', margin: 0, listStyle: 'none' }}>
          {guide.steps.map((step, i) => (
            <li key={i} style={{ display: 'flex', gap: 14, paddingBottom: 18, borderLeft: i < guide.steps.length - 1 ? '2px solid #e5e7eb' : 'none', marginLeft: 14, paddingLeft: 18, position: 'relative' }}>
              <div style={{ position: 'absolute', left: -16, top: 0, width: 30, height: 30, borderRadius: '50%', background: '#00D19F', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem' }}>{i + 1}</div>
              <div style={{ flex: 1, marginLeft: 18 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 4 }}>{step.title}</div>
                <div style={{ fontSize: '0.86rem', color: '#374151', lineHeight: 1.55 }}>{step.body}</div>
                {step.link && (
                  <a href={step.link.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, padding: '6px 12px', borderRadius: 6, background: '#eef2ff', color: '#4338ca', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>🔗 {step.link.label} ↗</a>
                )}
              </div>
            </li>
          ))}
        </ol>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', textAlign: 'right', position: 'sticky', bottom: 0, background: '#fff' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('sp_got_it', 'Got it')}</button>
        </div>
      </div>
    </div>
  )
}

function GatewayForm({ onSaved, onCancel }) {
  const { t } = useLanguage()
  const [provider, setProvider] = useState('stripe')
  const [body, setBody] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  function field(k) { return (e) => setBody(b => ({ ...b, [k]: e.target.value })) }

  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      await tournamentApi.put('/portal/reseller/payment/gateway', { provider, ...body })
      onSaved()
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally { setSubmitting(false) }
  }

  return (
    <form onSubmit={submit}>
      {err && <div style={errorBox}>{err}</div>}
      <Field label={t('sp_provider_row', 'Provider')}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ProviderTab id="stripe"  label="Stripe"  active={provider === 'stripe'}  onClick={() => { setProvider('stripe'); setBody({}) }} />
          <ProviderTab id="moneris" label="Moneris" active={provider === 'moneris'} onClick={() => { setProvider('moneris'); setBody({}) }} />
          <ProviderTab id="nmi"     label="NMI / Payroc" active={provider === 'nmi'} onClick={() => { setProvider('nmi'); setBody({}) }} />
          <ProviderTab id="elavon_converge" label="Elavon Converge" active={provider === 'elavon_converge'} onClick={() => { setProvider('elavon_converge'); setBody({}) }} />
          <ProviderTab id="elavon_fusebox" label="Elavon Fusebox" disabled hint={t('sp_elavon_fusebox_soon_hint', 'Coming soon — Elavon Fusebox requires an Elavon certification project. Talk to InteGolf to get started.')} />
        </div>
        <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, marginBottom: 0 }}>
          {t('sp_paypal_not_supported', "PayPal is not currently supported — its checkout requires sending customers to paypal.com, which breaks our seamless in-form payment flow. We'll revisit if PayPal exposes an in-form-only path.")}
        </p>
      </Field>

      {(provider === 'stripe' || provider === 'moneris') && (
        <PaymentSetupGuideButton guideKey={provider} label={t('sp_gateway_guide_label', 'Setup Guide — How to get your {provider} credentials', { provider: provider === 'stripe' ? 'Stripe' : 'Moneris' })} />
      )}
      {provider === 'stripe' && (
        <>
          <Field label={t('sp_publishable_key', 'Publishable key')} hint={t('sp_publishable_key_hint', 'Starts with pk_live_ or pk_test_')}><input style={{ ...inp, fontFamily: 'monospace' }} required value={body.publishableKey || ''} onChange={field('publishableKey')} placeholder="pk_test_…" /></Field>
          <Field label={t('sp_secret_key', 'Secret key')} hint={t('sp_secret_key_hint', 'Starts with sk_live_ or sk_test_')}><input type="password" style={{ ...inp, fontFamily: 'monospace' }} required value={body.secretKey || ''} onChange={field('secretKey')} placeholder="sk_test_…" /></Field>
        </>
      )}
      {provider === 'moneris' && (
        <>
          <Field label={t('sp_store_id', 'Store ID')}><input style={{ ...inp, fontFamily: 'monospace' }} required value={body.storeId || ''} onChange={field('storeId')} placeholder="store5" /></Field>
          <Field label={t('sp_api_token', 'API token')}><input type="password" style={{ ...inp, fontFamily: 'monospace' }} required value={body.apiToken || ''} onChange={field('apiToken')} placeholder="yesguy" /></Field>
          <Field label={t('sp_mode_label', 'Mode')}>
            <select value={body.mode || 'live'} onChange={field('mode')} style={{ ...inp, width: 200 }}>
              <option value="live">{t('live', 'Live')}</option>
              <option value="test">{t('sp_mode_test', 'Test')}</option>
            </select>
          </Field>
        </>
      )}
      {provider === 'nmi' && (
        <>
          <Field label={t('sp_nmi_security_key', 'Security key')} hint={t('sp_nmi_security_key_hint', 'Server-side only. From your gateway → Options → Settings → Security Keys.')}><input type="password" style={{ ...inp, fontFamily: 'monospace' }} required value={body.securityKey || ''} onChange={field('securityKey')} placeholder="6457Th…" /></Field>
          <Field label={t('sp_nmi_tokenization_key', 'Tokenization key')} hint={t('sp_nmi_tokenization_key_hint', 'Public key used by the checkout (Collect.js). Safe to expose.')}><input style={{ ...inp, fontFamily: 'monospace' }} value={body.tokenizationKey || ''} onChange={field('tokenizationKey')} placeholder="checkout_public_…" /></Field>
          <Field label={t('sp_nmi_base_url', 'Gateway URL')} hint={t('sp_nmi_base_url_hint', 'The URL you use to log into your gateway. Leave blank for secure.nmi.com.')}><input style={{ ...inp, fontFamily: 'monospace' }} value={body.baseUrl || ''} onChange={field('baseUrl')} placeholder="https://secure.nmi.com" /></Field>
        </>
      )}
      {provider === 'elavon_converge' && (
        <>
          <div style={{ padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e', marginBottom: 12, lineHeight: 1.5 }}>
            {t('sp_elavon_converge_note', 'You can save your Converge credentials now. Live customer checkout for Converge is enabled after a short one-time verification with Elavon.')}
          </div>
          <Field label={t('sp_elavon_account_id', 'Account ID (Merchant ID)')} hint={t('sp_elavon_account_id_hint', 'Your Converge ssl_account_id / merchant ID.')}><input style={{ ...inp, fontFamily: 'monospace' }} required value={body.accountId || ''} onChange={field('accountId')} placeholder="0012345…" /></Field>
          <Field label={t('sp_elavon_user_id', 'User ID')} hint={t('sp_elavon_user_id_hint', 'The Converge API user permitted to post transactions / session tokens.')}><input style={{ ...inp, fontFamily: 'monospace' }} required value={body.userId || ''} onChange={field('userId')} placeholder="apiuser" /></Field>
          <Field label={t('sp_elavon_pin', 'PIN')} hint={t('sp_elavon_pin_hint', 'Server-side only secret for that API user.')}><input type="password" style={{ ...inp, fontFamily: 'monospace' }} required value={body.pin || ''} onChange={field('pin')} placeholder="••••••" /></Field>
          <Field label={t('sp_elavon_vendor_id', 'Vendor ID (optional)')} hint={t('sp_elavon_vendor_id_hint', 'ssl_vendor_id, if Elavon issued you one.')}><input style={{ ...inp, fontFamily: 'monospace' }} value={body.vendorId || ''} onChange={field('vendorId')} placeholder="" /></Field>
          <Field label={t('sp_elavon_mode', 'Mode')} hint={t('sp_elavon_mode_hint', 'Use Demo while testing with an Elavon sandbox account.')}>
            <select value={body.demo ? 'demo' : 'live'} onChange={e => setBody(b => ({ ...b, demo: e.target.value === 'demo' }))} style={{ ...inp, width: 220 }}>
              <option value="live">{t('live', 'Live')}</option>
              <option value="demo">{t('sp_elavon_mode_demo', 'Demo / sandbox')}</option>
            </select>
          </Field>
        </>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        {onCancel && <button type="button" onClick={onCancel} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>}
        <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving', 'Saving…') : t('sp_save_gateway', 'Save gateway')}</button>
      </div>
    </form>
  )
}

// ─── Card on file (Mode B) ──────────────────────────────────────────────

function CardOnFileSection({ card, onChange }) {
  const { t } = useLanguage()
  const [editing, setEditing] = useState(!card?.configured)
  return (
    <div style={section}>
      <SectionHeader
        title={t('sp_card_section_title', 'Card on file (for InteGolf to charge)')}
        subtitle={t('sp_card_section_subtitle', "We charge this card via our Moneris merchant account when there's something we paid out on your behalf — Expedia hotel cost, golf rounds when WhoPaysCourse=InteGolf, etc.")}
      />
      {card?.configured && !editing
        ? (
          <div>
            <Row label={t('sp_card_row_label', 'Card')} value={t('sp_card_value', '{brand} ending in {last4}', { brand: card.brand?.toUpperCase(), last4: card.last4 })} />
            <Row label={t('sp_expiry', 'Expiry')} value={`${String(card.expiryMonth).padStart(2, '0')}/${card.expiryYear}`} />
            <Row label={t('sp_cardholder_row', 'Cardholder')} value={card.cardholderName} />
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(true)} style={primaryBtn}>{t('sp_replace', 'Replace')}</button>
              <button onClick={async () => {
                if (!confirm(t('sp_remove_card_confirm', 'Remove the card on file?'))) return
                try { await tournamentApi.delete('/portal/reseller/payment/card'); onChange() }
                catch (err) { alert(err?.response?.data?.message || err.message) }
              }} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove', 'Remove')}</button>
            </div>
          </div>
        )
        : <CardForm onSaved={() => { setEditing(false); onChange() }} onCancel={card?.configured ? () => setEditing(false) : null} />
      }
    </div>
  )
}

function CardForm({ onSaved, onCancel }) {
  const { t } = useLanguage()
  const [pan, setPan] = useState('')
  const [expiryMonth, setExpiryMonth] = useState('')
  const [expiryYear, setExpiryYear] = useState('')
  const [cardholderName, setCardholderName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      await tournamentApi.put('/portal/reseller/payment/card', {
        pan, expiryMonth: Number(expiryMonth), expiryYear: Number(expiryYear), cardholderName,
      })
      onSaved()
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally { setSubmitting(false) }
  }

  return (
    <form onSubmit={submit}>
      {err && <div style={errorBox}>{err}</div>}
      <PaymentSetupGuideButton guideKey="card" label={t('sp_card_guide_label', 'Setup Guide — What card to use + how charges work')} />
      <Field label={t('sp_cardholder_name', 'Cardholder name')}><input style={inp} required value={cardholderName} onChange={e => setCardholderName(e.target.value)} placeholder={t('sp_cardholder_name_placeholder', 'As shown on card')} /></Field>
      <Field label={t('sp_card_number', 'Card number')}><input style={{ ...inp, fontFamily: 'monospace' }} required value={pan} onChange={e => setPan(e.target.value)} placeholder="4242 4242 4242 4242" /></Field>
      <div style={row}>
        <Field label={t('sp_expiry_month', 'Expiry month')} style={{ flex: 1 }}>
          <select value={expiryMonth} onChange={e => setExpiryMonth(e.target.value)} required style={inp}>
            <option value="">--</option>
            {Array.from({ length: 12 }).map((_, i) => <option key={i+1} value={i+1}>{String(i+1).padStart(2, '0')}</option>)}
          </select>
        </Field>
        <Field label={t('sp_expiry_year_4_digit', 'Expiry year (4-digit)')} style={{ flex: 1 }}>
          <input style={inp} required type="number" min={2026} max={2050} value={expiryYear} onChange={e => setExpiryYear(e.target.value)} placeholder="2028" />
        </Field>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
        {t('sp_cvv_not_stored_note', 'CVV is not stored. The card number is encrypted at rest with AES-256-GCM.')}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        {onCancel && <button type="button" onClick={onCancel} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>}
        <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving', 'Saving…') : t('sp_save_card', 'Save card')}</button>
      </div>
    </form>
  )
}

// ─── Shared bits ─────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }) {
  return (
    <>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {subtitle && <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{subtitle}</p>}
    </>
  )
}
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', fontSize: 14, borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 200, color: '#64748b' }}>{label}</div>
      <div style={{ flex: 1, color: '#0f172a', fontWeight: 500 }}>{value}</div>
    </div>
  )
}
function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}
const PROVIDER_LABELS = {
  stripe: 'Stripe', moneris: 'Moneris', nmi: 'NMI / Payroc',
  elavon_converge: 'Elavon Converge', elavon_fusebox: 'Elavon Fusebox',
}

function ProviderTab({ id, label, active, disabled, hint, onClick }) {
  const { t } = useLanguage()
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} title={hint} style={{
      padding: '10px 16px',
      background: active ? '#0f172a' : '#fff', color: active ? '#fff' : (disabled ? '#94a3b8' : '#475569'),
      border: '1px solid ' + (active ? '#0f172a' : '#e2e8f0'), borderRadius: 6,
      fontSize: 13, fontWeight: active ? 600 : 400, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}>{label}{disabled && ' ' + t('sp_provider_tab_soon', '(soon)')}</button>
  )
}

const h2 = { margin: '0 0 12px', fontSize: 16, color: '#0f172a' }
const section = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const row = { display: 'flex', gap: 12 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
